import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../context/TranslationContext';
import JSONViewer from '../JsonViewer';
import CacheGroupDisplay from '../CacheGroupDisplay';
import { Button } from '../';
import MessageLog from '../MessageLog';
import debug from '@commons-shared/debug';
import { config } from '../../config/environment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faTrash, faSearch, faDatabase, faMemory, faFolder } from '@fortawesome/free-solid-svg-icons';

/**
 * Composant générique pour afficher et gérer différentes sources de données
 * Supporte localStorage, waItems, et d'autres sources de données
 */
export default function AdminDataTab({ dataSources = [] }) {
  const { t } = useTranslation();
  const [selectedSource, setSelectedSource] = useState(0);
  const [sourceData, setSourceData] = useState({});
  const [filteredData, setFilteredData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshCount, setRefreshCount] = useState(0);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [expandedEntries, setExpandedEntries] = useState({});

  // Source de données courante
  const currentSource = dataSources[selectedSource];

  /**
   * Regroupe les entrées par préfixe (premier niveau avant le underscore)
   * Ex: "bel_tags" et "bel_shortcuts" -> groupe "bel"
   * Ex: "wa_world" et "wa_manuscript" -> groupe "wa"
   */
  const groupDataEntries = useCallback((data) => {
    const groups = {};
    
    Object.entries(data).forEach(([key, value]) => {
      // Extraire le préfixe (avant le premier underscore)
      const prefix = key.includes('_') ? key.split('_')[0] : 'other';
      
      if (!groups[prefix]) {
        groups[prefix] = [];
      }
      
      groups[prefix].push({
        key,
        data: value,
        isValid: true, // Toujours valide pour les données localStorage
        size: JSON.stringify(value).length,
        expiry: Date.now() + (365 * 24 * 60 * 60 * 1000) // Pas vraiment applicable, mais nécessaire pour CacheGroupDisplay
      });
    });
    
    return groups;
  }, []);

  /**
   * Obtient l'icône appropriée selon le type de groupe
   */
  const getGroupIcon = useCallback((groupName) => {
    if (groupName.includes('bel')) return faMemory;
    if (groupName.includes('wa')) return faDatabase;
    if (groupName.includes('ai') || groupName.includes('vision') || groupName.includes('text') || groupName.includes('translation')) return faMemory;
    return faFolder;
  }, []);

  /**
   * Obtient le style de couleur selon le type de groupe (variables sémantiques)
   */
  const getGroupColorStyle = useCallback((groupName) => {
    if (groupName.includes('bel')) return { backgroundColor: 'var(--color-info-light)', color: 'var(--color-info)' };
    if (groupName.includes('wa')) return { backgroundColor: 'var(--color-accent-light)', color: 'var(--color-accent)' };
    if (groupName.includes('ai') || groupName.includes('vision') || groupName.includes('text') || groupName.includes('translation')) return { backgroundColor: 'var(--color-success-light)', color: 'var(--color-success)' };
    return { backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)' };
  }, []);

  /**
   * Formate la taille en octets de manière lisible
   */
  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  /**
   * Formate une durée (non utilisé pour les données localStorage mais nécessaire pour CacheGroupDisplay)
   */
  const formatDuration = (ms) => {
    return '-';
  };

  // Fonction pour charger les données de la source courante
  const loadSourceData = useCallback(async () => {
    if (!currentSource || !currentSource.loader) {
      setSourceData({});
      return;
    }

    try {
      const data = await currentSource.loader();
      setSourceData(data || {});
      setRefreshCount(prev => prev + 1);
      debug.log(config, `Données chargées pour ${currentSource.name}`, { keyCount: Object.keys(data || {}).length });
    } catch (error) {
      setSourceData({});
      setMessage({ type: 'error', text: t('LOG_VIEWER', 'LOAD_ERROR', `Erreur lors du chargement de {{source}}`, { source: currentSource.name }) });
    }
  }, [currentSource]);

  // Fonction pour filtrer les données selon le terme de recherche
  const filterData = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredData(sourceData);
      return;
    }

    const filtered = {};
    const lowerSearchTerm = searchTerm.toLowerCase();

    Object.keys(sourceData).forEach(key => {
      const lowerKey = key.toLowerCase();
      const valueString = JSON.stringify(sourceData[key]).toLowerCase();
      
      if (lowerKey.includes(lowerSearchTerm) || valueString.includes(lowerSearchTerm)) {
        filtered[key] = sourceData[key];
      }
    });

    setFilteredData(filtered);
  }, [sourceData, searchTerm]);

  // Fonction pour supprimer une clé spécifique
    // Fonction pour basculer l'expansion d'une entrée
  const toggleEntryExpansion = (key) => {
    setExpandedEntries(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const deleteKey = async (key) => {
    if (!currentSource || !currentSource.deleter) {
      setMessage({ type: 'warning', text: t('LOG_VIEWER', 'DELETE_NOT_SUPPORTED', 'Suppression non supportée pour cette source de données.') });
      return;
    }

    if (window.confirm(t('LOG_VIEWER', 'CONFIRM_DELETE_KEY', `Êtes-vous sûr de vouloir supprimer "{{key}}" ?`, { key }))) {
      try {
        await currentSource.deleter(key);
        loadSourceData();
        setMessage({ type: 'success', text: t('LOG_VIEWER', 'KEY_DELETED_SUCCESS', `Clé "{{key}}" supprimée avec succès`, { key }) });
      } catch (error) {
        setMessage({ type: 'error', text: t('LOG_VIEWER', 'DELETE_ERROR', 'Erreur lors de la suppression') });
      }
    }
  };

  // Fonction pour supprimer toutes les clés
  const clearAllKeys = async () => {
    if (!currentSource || !currentSource.clearAll) {
      setMessage({ type: 'warning', text: t('LOG_VIEWER', 'CLEAR_ALL_NOT_SUPPORTED', 'Suppression en masse non supportée pour cette source de données.') });
      return;
    }

    const keyCount = Object.keys(sourceData).length;
    if (keyCount === 0) {
      setMessage({ type: 'info', text: t('LOG_VIEWER', 'NO_DATA_TO_DELETE', 'Aucune donnée à supprimer.') });
      return;
    }

    if (window.confirm(t('LOG_VIEWER', 'CONFIRM_DELETE_ALL', `Êtes-vous sûr de vouloir supprimer toutes les {{count}} entrées ?`, { count: keyCount }))) {
      try {
        await currentSource.clearAll();
        loadSourceData();
        setMessage({ type: 'success', text: t('LOG_VIEWER', 'ALL_ENTRIES_DELETED', `{{count}} entrées supprimées avec succès`, { count: keyCount }) });
      } catch (error) {
        setMessage({ type: 'error', text: t('LOG_VIEWER', 'CLEAR_ALL_ERROR', 'Erreur lors de la suppression en masse') });
      }
    }
  };

  // Fonction pour exporter les données
  const exportData = () => {
    if (!currentSource) return;

    const dataToExport = {
      source: currentSource.name,
      exportDate: new Date().toISOString(),
      keysCount: Object.keys(sourceData).length,
      data: sourceData
    };

    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${currentSource.name.toLowerCase()}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setMessage({ type: 'success', text: t('LOG_VIEWER', 'DATA_EXPORTED', `Données exportées: {{count}} entrées`, { count: Object.keys(sourceData).length }) });
  };

  // Fonction pour copier une entrée spécifique dans le presse-papier
  const copyEntryToClipboard = async (key, value) => {
    try {
      const entryData = {
        key: key,
        value: value,
        source: currentSource?.name,
        copyDate: new Date().toISOString()
      };

      const jsonString = JSON.stringify(entryData, null, 2);
      
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(jsonString);
        setMessage({ type: 'success', text: t('LOG_VIEWER', 'ENTRY_COPY_SUCCESS', `Entrée "{{key}}" copiée dans le presse-papier`, { key }) });
      } else {
        // Fallback pour les navigateurs plus anciens ou contextes non-sécurisés
        const textArea = document.createElement('textarea');
        textArea.value = jsonString;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          setMessage({ type: 'success', text: t('LOG_VIEWER', 'ENTRY_COPY_SUCCESS', `Entrée "{{key}}" copiée dans le presse-papier`, { key }) });
        } catch (err) {
          console.error('Erreur lors de la copie:', err);
          setMessage({ type: 'error', text: t('LOG_VIEWER', 'COPY_ERROR', 'Erreur lors de la copie dans le presse-papier') });
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la copie de l\'entrée:', error);
      setMessage({ type: 'error', text: t('LOG_VIEWER', 'COPY_ERROR', 'Erreur lors de la copie dans le presse-papier') });
    }
  };

  // Fonction pour copier dans le presse-papier
  const copyToClipboard = async () => {
    if (!currentSource) return;

    try {
      const dataToExport = {
        source: currentSource.name,
        exportDate: new Date().toISOString(),
        keysCount: Object.keys(filteredData).length,
        searchTerm: searchTerm || null,
        data: filteredData
      };

      const jsonString = JSON.stringify(dataToExport, null, 2);
      
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(jsonString);
        setMessage({ type: 'success', text: t('LOG_VIEWER', 'COPY_SUCCESS', 'Données copiées dans le presse-papier') });
      } else {
        // Fallback pour les navigateurs plus anciens ou contextes non-sécurisés
        const textArea = document.createElement('textarea');
        textArea.value = jsonString;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          document.execCommand('copy');
          setMessage({ type: 'success', text: t('LOG_VIEWER', 'COPY_SUCCESS', 'Données copiées dans le presse-papier') });
        } catch (err) {
          setMessage({ type: 'error', text: t('LOG_VIEWER', 'COPY_ERROR', 'Erreur lors de la copie') });
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('LOG_VIEWER', 'COPY_ERROR', 'Erreur lors de la copie') });
    }
  };

  // Charger les données quand la source change
  useEffect(() => {
    loadSourceData();
  }, [loadSourceData]);

  // Filtrer les données quand le terme de recherche change
  useEffect(() => {
    filterData();
  }, [filterData]);

  // Reset search when source changes
  useEffect(() => {
    setSearchTerm('');
  }, [selectedSource]);

  if (!dataSources || dataSources.length === 0) {
    return (
      <div className="wa-admin-data-tab">
        <div className="wa-admin-empty">
          <p>{t('LOG_VIEWER', 'NO_DATA_SOURCE', 'Aucune source de données configurée.')}</p>
        </div>
      </div>
    );
  }

  const keyCount = Object.keys(sourceData).length;
  const filteredKeyCount = Object.keys(filteredData).length;

  return (
    <div className="wa-admin-data-tab">
      {/* Sélecteur de source de données */}
      {dataSources.length > 1 && (
        <div className="wa-admin-source-selector mb-4">
          <div className="flex flex-wrap gap-2">
            {dataSources.map((source, index) => (
              <Button
                key={index}
                variant={selectedSource === index ? "primary" : "secondary"}
                size="sm"
                onClick={() => setSelectedSource(index)}
                className="text-sm"
              >
                <FontAwesomeIcon icon={source.icon || faDatabase} className="mr-2" />
                {source.name}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* En-tête avec statistiques et actions */}
      <div className="wa-admin-header">
        <div className="wa-admin-stats">
          <h3 className="flex items-center">
            <FontAwesomeIcon icon={currentSource?.icon || faDatabase} className="mr-2" />
            {currentSource?.name || 'Source de données'}
          </h3>
          <p className="wa-admin-subtitle">
            {currentSource?.description}
          </p>
          <p className="text-sm text-gray-600">
            {keyCount === 0 
              ? t('LOG_VIEWER', 'NO_DATA_FOUND', 'Aucune donnée trouvée')
              : filteredKeyCount !== keyCount
                ? t('LOG_VIEWER', 'FILTERED_KEYS_COUNT', '{{filtered}} sur {{total}} entrées affichées', { filtered: filteredKeyCount, total: keyCount })
                : t('LOG_VIEWER', 'TOTAL_KEYS_COUNT', '{{count}} entrée(s) trouvée(s)', { count: keyCount })
            }
          </p>
        </div>
        
        <div className="wa-admin-actions">
          <Button
            variant="secondary"
            onClick={loadSourceData}
            title={t('LOG_VIEWER', 'REFRESH_DATA', 'Actualiser les données')}
          >
            <FontAwesomeIcon icon={faRefresh} />
            <span className="hidden sm:inline ml-2">{t('LOG_VIEWER', 'REFRESH', 'Actualiser')}</span>
          </Button>
          
          {keyCount > 0 && (
            <>
              {currentSource?.clearAll && (
                <Button
                  variant="danger"
                  onClick={clearAllKeys}
                  title={t('LOG_VIEWER', 'CLEAR_ALL_BEL_KEYS', 'Supprimer toutes les données')}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  <span className="hidden sm:inline ml-2">{t('LOG_VIEWER', 'CLEAR_ALL', 'Tout supprimer')}</span>
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Barre de recherche */}
      {keyCount > 0 && (
        <div className="wa-admin-search">
          <div className="wa-search-field">
            <FontAwesomeIcon icon={faSearch} className="wa-search-icon" />
            <input
              type="text"
              placeholder={t('LOG_VIEWER', 'SEARCH_KEYS', 'Rechercher dans les clés ou valeurs...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="wa-input wa-search-input"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchTerm('')}
                title={t('LOG_VIEWER', 'CLEAR_SEARCH', 'Effacer la recherche')}
                className="wa-search-clear"
              >
                ×
              </Button>
            )}
          </div>
          {searchTerm && (
            <div className="wa-search-status">
              {filteredKeyCount === 0 
                ? t('LOG_VIEWER', 'NO_SEARCH_RESULTS_FOR', `Aucun résultat pour "{{search}}"`, { search: searchTerm })
                : t('LOG_VIEWER', 'SEARCH_RESULTS_COUNT', `{{count}} résultat(s) pour "{{search}}"`, { count: filteredKeyCount, search: searchTerm })
              }
            </div>
          )}
        </div>
      )}

      {/* Affichage des données */}
      <div className="wa-admin-content">
        {keyCount === 0 ? (
          <div className="wa-admin-empty">
            <p>{t('LOG_VIEWER', 'NO_DATA_AVAILABLE', 'Aucune donnée disponible pour cette source.')}</p>
            {currentSource?.emptyMessage && (
              <p className="text-sm text-gray-500 mt-2">
                {currentSource.emptyMessage}
              </p>
            )}
          </div>
        ) : filteredKeyCount === 0 ? (
          <div className="wa-admin-empty">
            <p>{t('LOG_VIEWER', 'NO_SEARCH_RESULTS', 'Aucun résultat trouvé pour cette recherche.')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {(() => {
              // Regrouper les données filtrées
              const groups = groupDataEntries(filteredData);
              // Trier les groupes par nom
              const sortedGroupNames = Object.keys(groups).sort();
              
              return sortedGroupNames.map((groupName) => (
                <CacheGroupDisplay
                  key={groupName}
                  groupName={groupName}
                  entries={groups[groupName]}
                  formatBytes={formatBytes}
                  formatDuration={formatDuration}
                  onClear={currentSource?.deleter ? (pattern) => {
                    // Supprimer toutes les clés du groupe
                    const keysToDelete = groups[groupName].map(entry => entry.key);
                    if (window.confirm(t('LOG_VIEWER', 'CONFIRM_DELETE_GROUP', `Êtes-vous sûr de vouloir supprimer toutes les {{count}} entrées du groupe "{{group}}" ?`, { count: keysToDelete.length, group: groupName }))) {
                      Promise.all(keysToDelete.map(key => currentSource.deleter(key)))
                        .then(() => {
                          loadSourceData();
                          setMessage({ type: 'success', text: t('LOG_VIEWER', 'GROUP_DELETED_SUCCESS', `Groupe "{{group}}" supprimé avec succès ({{count}} entrées)`, { group: groupName, count: keysToDelete.length }) });
                        })
                        .catch(error => {
                          setMessage({ type: 'error', text: t('LOG_VIEWER', 'DELETE_ERROR', 'Erreur lors de la suppression') });
                        });
                    }
                  } : null}
                  isClearing={false}
                  t={t}
                  colorStyle={getGroupColorStyle(groupName)}
                  icon={getGroupIcon(groupName)}
                />
              ));
            })()}
          </div>
        )}
      </div>

      {/* Informations de débogage */}
      <div className="wa-admin-debug-info">
        <small className="text-gray-500">
          {t('LOG_VIEWER', 'LAST_REFRESH', 'Dernière actualisation')}: {new Date().toLocaleTimeString()} 
          {refreshCount > 1 && ` (${refreshCount})`}
        </small>
      </div>

      {/* Messages utilisateur */}
      <MessageLog 
        type={message.type} 
        text={message.text} 
        autoHide={true} 
        duration={4000} 
      />
    </div>
  );
}
